import { type CashFlowCategory } from '@progear/finance-core';
import { LINE_META } from '@/components/cash-flow/categoryMeta';

/**
 * Register-lens grouping (E1). The register can group its rows by ONE facet —
 * direction, line or project — each group getting a header + a subtotal row. This
 * is a register-appropriate affordance under L14 (the matrix already groups by the
 * 7 named lines, the calendar by day); the underlying numbers a subtotal shows are
 * still the SHARED finance-core aggregates (`signedSumEntries` / `sumSettlement`),
 * never a component-side sum. This module is PURE PRESENTATION: it only buckets and
 * labels rows — it does NO money math (L9), so it carries no `Money` at all.
 */
export type LedgerGroupKey = 'none' | 'suund' | 'liik' | 'project';

/** The group-by choices in display order — drives the register's "Rühmita" control. */
export const LEDGER_GROUP_OPTIONS: [LedgerGroupKey, string][] = [
  ['none', 'Puudub'],
  ['suund', 'Suund'],
  ['liik', 'Liik'],
  ['project', 'Projekt'],
];

/** The minimal row shape grouping reads — direction, line and project. The full
 *  `LedgerRow` satisfies it structurally, so grouping needs no money/date fields. */
export interface LedgerGroupable {
  suund: 'income' | 'cost';
  category: CashFlowCategory;
  project: string | null;
}

/** One rendered group: a stable id, an Estonian label, the (direction, when grouped
 *  by Suund) and the rows it owns — in the order they arrived (already sorted). */
export interface LedgerGroup<R> {
  id: string;
  label: string;
  suund?: 'income' | 'cost';
  rows: R[];
}

/** The id used for rows with no project, so they bucket together (label "Projektita"). */
const PROJECTLESS = '\u2014';

/** Income sorts before cost when grouping by direction (a single rank, so the sort
 *  comparator itself stays branch-free). */
const dirRank = (id: string): number => (id === 'income' ? 0 : 1);

const groupId = (row: LedgerGroupable, key: Exclude<LedgerGroupKey, 'none'>): string =>
  key === 'suund' ? row.suund : key === 'liik' ? row.category : (row.project ?? PROJECTLESS);

const groupLabel = (key: Exclude<LedgerGroupKey, 'none'>, id: string): string =>
  key === 'suund'
    ? id === 'income'
      ? 'Tulu'
      : 'Kulu'
    : key === 'liik'
      ? LINE_META[id as CashFlowCategory].label
      : id === PROJECTLESS
        ? 'Projektita'
        : id;

/**
 * Bucket already-sorted rows into display groups. `none` returns ONE pass-through
 * group (the flat list), so the caller renders it identically to the ungrouped
 * table. Otherwise rows keep their incoming order inside each group; the groups
 * themselves keep first-seen order, except by direction where income leads. Pure.
 */
export function groupLedgerRows<R extends LedgerGroupable>(
  rows: R[],
  key: LedgerGroupKey,
): LedgerGroup<R>[] {
  if (key === 'none') return [{ id: 'all', label: '', rows }];
  const order: string[] = [];
  const map = new Map<string, R[]>();
  for (const row of rows) {
    const id = groupId(row, key);
    let bucket = map.get(id);
    if (!bucket) {
      bucket = [];
      map.set(id, bucket);
      order.push(id);
    }
    bucket.push(row);
  }
  if (key === 'suund') order.sort((a, b) => dirRank(a) - dirRank(b));
  return order.map((id) => ({
    id,
    label: groupLabel(key, id),
    suund: key === 'suund' ? (id as 'income' | 'cost') : undefined,
    rows: map.get(id)!,
  }));
}
